/**
 * Rails list component for managing vertical scrolling of multiple rails.
 * Handles vertical navigation between rails, focus management, and transform animations.
 * Manages the list of rails and coordinates with individual rail sublists.
 */

import Direction from '../../../../../shared/types/enums/direction';
import Focus from '../../../../../shared/types/enums/focus';
import FocusUtils from '@shared/utils/focus';
import List from '../list';
import Main from '../../../../../shared/types/enums/main';
import MainContext from '../../../../../shared/components/utils/context/mainContext';
import RailListContext from '../../../../../shared/components/utils/context/railListContext';
import Rail from '../../../../../shared/classes/rail';
import RailListContainer from '../../../atoms/container/railListContainer';
import RailsSubList from './railsSublist';
import closeMenu from '../../../../../shared/actions/menu/closeMenu';
import useTransform from '../../../../../shared/components/utils/hooks/useTransform';
import MODIFICATION from '../../../../../shared/types/enums/modification';
import { floatingMenuSelector } from '../../../../../shared/store/slices/floatingMenu.slice';
import { routerSelector } from '../../../../../shared/store/slices/router.slice';
import { setFocus } from '../../../../../shared/store/slices/focus.slice';
import {
  RailDisplayMode,
  RailType,
} from '../../../../../shared/types/enums/rail';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../shared/store/hooks';
import {
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Vertical offset values for different rail display modes.
 * Used to calculate scroll position when navigating between rails.
 */
const OFFSET = {
  addonPhoto: 290,
  cleanPhoto: 290,
  channelGuidePhoto: 290,
  character: 300,
  filter: 95,
  mosaic: 500,
  photo: 290,
  poster: 390,
  progressPhoto: 290,
  square: 202,
  title: 58,
};

interface Props {
  currentRail?: MutableRefObject<number>;
  rails: Rail[];
  onItemClick?: (
    horizontalIndex: number,
    verticalIndex: number,
    focus: number | (string | number)[] | number[] | null
  ) => void;
  onMouseEnter?: () => void;
}

function RailsList({ currentRail, rails, onItemClick, onMouseEnter }: Props) {
  const dispatch = useAppDispatch();
  const { open } = useAppSelector(floatingMenuSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const { outOfBoundsAction, onChangeElement } = useContext(MainContext);
  const [railsData, setRailsData] = useState(rails);

  // Misc

  const previousParams = currentParams as {
    previousFocus: number[];
    verticalIndex: number;
  };
  const initialIndex = previousParams?.previousFocus?.length
    ? previousParams?.previousFocus.includes(Focus.RAILS_LIST)
      ? previousParams?.verticalIndex
      : 0
    : 0;

  // References

  const railsRef = useRef<Rail[]>(railsData);
  const indexRef = useRef(initialIndex);
  const setListIndex = useRef<(newIndex: number, increment?: number) => void>(
    () => null
  );
  railsRef.current = rails;

  // Actions

  const modifyRails = (type: MODIFICATION, id: number) => {
    const newData = railsRef.current.filter((item, index) => {
      return index !== id;
    });
    if (!newData.length) {
      return;
    }
    if (id === newData.length) {
      setListIndex.current(0, -1);
    }

    setTimeout(() => {
      railsRef.current = newData;
      setRailsData(newData);
    });
  };

  // Event handlers

  const onChange = (index: number) => {
    setTimeout(() => {
      if (currentRail) {
        currentRail.current = index;
      }

      // Set new focus
      dispatch(
        setFocus([
          Focus.RAILS_LIST,
          parseInt(`${Focus.RAILS_SUB_LIST}${index}`),
        ])
      );
      // Set new style
      const offset = railsRef.current
        .slice(0, index)
        .reduce((prev, current, index) => {
          if (current?.type === RailType.FILTER) {
            return prev;
          }
          if (
            index > 0 &&
            railsRef.current[index - 1].type === RailType.FILTER
          ) {
            return (
              prev +
              OFFSET[current.displayMode] +
              OFFSET['filter'] -
              (!current?.title ? OFFSET['title'] : 0)
            );
          }
          return (
            prev +
            (current?.type === RailType.MOSAIC
              ? OFFSET['mosaic']
              : OFFSET[current.displayMode as RailDisplayMode.PHOTO])
          );
        }, 0);
      setStyle(offset);
      onChangeElement(Main.RAILS, null, index);
    });
  };

  const onMouseEnterFallback = () => {
    if (onMouseEnter) {
      onMouseEnter();
    } else if (!open) {
      closeMenu();
      dispatch(
        setFocus([
          Focus.RAILS_LIST,
          parseInt(
            Focus.RAILS_SUB_LIST + (currentRail?.current.toString() || '')
          ),
        ])
      );
    }
  };

  // Effect hook

  useEffect(() => {
    setRailsData(rails);
  }, [rails]);

  // Transform hook

  const offset = useMemo(() => {
    let offset = 0;
    // Add every rail offset up and exclude non existing titles
    for (let i = 0; i < indexRef.current; i++) {
      offset += OFFSET[rails[i]?.displayMode];
      if (!rails[i]?.title && rails[i]?.type !== RailType.FILTER) {
        offset -= OFFSET['title'];
      }
    }
    // Exclude filter offset if displayed on screen
    if (
      indexRef.current > 0 &&
      rails[indexRef.current - 1].type === RailType.FILTER
    ) {
      offset -= OFFSET['filter'];
    }
    return offset;
  }, []);
  const [style, , , , setStyle] = useTransform(1, offset, Direction.VERTICAL);

  return (
    <RailListContainer
      focused={FocusUtils.evaluateFocus(Focus.RAILS_LIST)}
      animation={'rail-scroll'}
      style={style}
      onMouseEnter={onMouseEnterFallback}
    >
      <RailListContext.Provider value={{ setListIndex }}>
        <List
          direction={Direction.VERTICAL}
          focus={Focus.RAILS_LIST}
          initialIndex={indexRef.current}
          numberOfPressures={5}
          outOfBounds={outOfBoundsAction}
          onChange={onChange}
          setListIndex={setListIndex}
        >
          {railsData.map((item, index) => {
            return (
              <RailsSubList
                key={index}
                listId={index}
                rail={item}
                modifyRails={modifyRails}
                onItemClick={onItemClick}
              />
            );
          })}
        </List>
      </RailListContext.Provider>
    </RailListContainer>
  );
}

export default RailsList;
